import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Checkbox, Link, Typography, useGlobal, SliderComponent } from '@rcg/ui-react';
import { useReactiveVar } from '@apollo/client';
import { formatPrice } from '@rcg/pricing-format';
import useStyles from './useStyles';
import Dictionary from '../../utils/dictionary';
import { filtersVar, searchResultsVar } from '../../operations/localState/reactiveVariables';
import { setFilters, clearFilters } from '../../operations/localState/setFilters';
import setFilterPanel from '../../operations/localState/setFilterPanel';
import { MILLISECONDS_IN_AN_HOUR, getTimeRangeLabelForFilter } from '../../utils/timeFormats';
import { PRICE_FILTER_STEP } from '../../utils/constants';
import InfoTooltip from '../InfoTooltip';

export default function FiltersPanel() {
  const classes = useStyles();
  const {
    renderContent,
    data: { localizedDayjs },
  } = useGlobal();
  const searchResults = searchResultsVar();
  const filters = useReactiveVar(filtersVar);
  const [showAllAirlines, setShowAllAirlines] = useState(false);
  const stopsFilters = filters.stops;
  const airlineFilters = filters.airlines;
  const minMaxPriceFilters = filters.minMaxPrice;
  const flightTimesFilters = filters.flightTimes;
  const [priceValue, setPriceValue] = useState([]);
  const [priceCommitted, setPriceCommitted] = useState([]);
  const [takeOffTimesValue, setTakeOffTimesValue] = useState([]);
  const [takeOffTimesCommitted, setTakeOffTimesCommitted] = useState([]);
  const [landingTimesValue, setLandingTimesValue] = useState([]);
  const [landingTimesCommitted, setLandingTimesCommitted] = useState([]);
  const abbreviatedWeekDays = localizedDayjs.localeData().weekdaysShort();

  const getAirportCode = (shouldGetTakeOff) => {
    let takeOffAirport = '---';
    const segmentAirportType = shouldGetTakeOff ? 'departure' : 'arrival';
    const segments =
      searchResults &&
      searchResults.matchingFlights &&
      searchResults.matchingFlights[0] &&
      searchResults.matchingFlights[0].flightSegments;

    if (segments) {
      takeOffAirport =
        segments[shouldGetTakeOff ? 0 : segments.length - 1][segmentAirportType].airport.code;
    }

    return takeOffAirport;
  };

  const toggleStopFilterSelection = (event, stopFilterName) => {
    const selected = event.target.checked;
    const newFilters = {
      ...filters,
      stops: {
        ...stopsFilters,
        [stopFilterName]: { ...stopsFilters[stopFilterName], selected },
      },
    };
    setFilters(newFilters);
  };

  const toggleAirlineFilterSelection = (event, airlineFilterCode) => {
    const selected = event.target.checked;
    const newFilters = {
      ...filters,
      airlines: airlineFilters.map((airlineFilter) =>
        airlineFilter.code === airlineFilterCode ? { ...airlineFilter, selected } : airlineFilter,
      ),
    };
    setFilters(newFilters);
  };

  const toggleShowMoreFlag = () => setShowAllAirlines(!showAllAirlines);

  const handleTakeOffTimesChange = (_event, newValue) => {
    if (
      (newValue[0] !== takeOffTimesValue[0] || newValue[1] !== takeOffTimesValue[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setTakeOffTimesValue(newValue);
    }
  };
  const handleTakeOffTimesCommittedChange = (_event, newValue) => {
    if (
      (newValue[0] !== takeOffTimesCommitted[0] || newValue[1] !== takeOffTimesCommitted[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setTakeOffTimesCommitted(newValue);
    }
  };

  useEffect(() => {
    if (takeOffTimesCommitted.length === 2) {
      const newFilters = {
        ...filters,
        flightTimes: {
          ...flightTimesFilters,
          takeOff: {
            ...flightTimesFilters.takeOff,
            selected: takeOffTimesCommitted,
          },
        },
      };
      setFilters(newFilters);
    }
    // eslint-disable-next-line
  }, [takeOffTimesCommitted]);

  useEffect(() => {
    setTakeOffTimesValue(flightTimesFilters.takeOff.selected);
    // eslint-disable-next-line
  }, [flightTimesFilters.takeOff.selected]);

  const handleLandingTimesChange = (_event, newValue) => {
    if (
      (newValue[0] !== landingTimesValue[0] || newValue[1] !== landingTimesValue[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setLandingTimesValue(newValue);
    }
  };

  const handleLandingTimesCommittedChange = (_event, newValue) => {
    if (
      (newValue[0] !== landingTimesCommitted[0] || newValue[1] !== landingTimesCommitted[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setLandingTimesCommitted(newValue);
    }
  };

  useEffect(() => {
    if (landingTimesCommitted.length === 2) {
      const newFilters = {
        ...filters,
        flightTimes: {
          ...flightTimesFilters,
          landing: {
            ...flightTimesFilters.landing,
            selected: landingTimesCommitted,
          },
        },
      };
      setFilters(newFilters);
    }
    // eslint-disable-next-line
  }, [landingTimesCommitted]);

  useEffect(() => {
    setLandingTimesValue(flightTimesFilters.landing.selected);
    // eslint-disable-next-line
  }, [flightTimesFilters.landing.selected]);

  const handlePriceChange = (_event, newValue) => {
    if (
      (newValue[0] !== priceValue[0] || newValue[1] !== priceValue[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setPriceValue(newValue);
    }
  };

  const handlePriceCommittedChange = (_event, newValue) => {
    if (
      (newValue[0] !== priceCommitted[0] || newValue[1] !== priceCommitted[1]) &&
      newValue[0] !== newValue[1]
    ) {
      setPriceCommitted(newValue);
    }
  };

  useEffect(() => {
    if (priceCommitted.length === 2) {
      const newFilters = {
        ...filters,
        minMaxPrice: {
          ...minMaxPriceFilters,
          selected: priceCommitted,
        },
      };
      setFilters(newFilters);
    }
    // eslint-disable-next-line
  }, [priceCommitted]);

  useEffect(() => {
    setPriceValue(minMaxPriceFilters.selected);
    // eslint-disable-next-line
  }, [minMaxPriceFilters.selected]);

  useEffect(() => {
    setFilterPanel({ isOpen: true });
    return () => {
      setFilterPanel({ isOpen: false });
    };
  }, []);

  return (
    <div className={classes.panel}>
      <div className={classes.filtersPanelHeader}>
        <Typography variant="h5" className={classes.filtersPanelTitle}>
          {renderContent(Dictionary.airFilterByLabel.key, Dictionary.airFilterByLabel.defaultValue)}
        </Typography>
        {airlineFilters.length > 0 && (
          /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
          <Link
            data-testid="clear-filters-button"
            component="button"
            underline="none"
            className={`${classes.clearFiltersButton}`}
            onClick={clearFilters}>
            <span>
              {renderContent(
                Dictionary.airClearAllFiltersLabel.key,
                Dictionary.airClearAllFiltersLabel.defaultValue,
              )}
            </span>
          </Link>
        )}
      </div>
      <div className={classes.filterContainer}>
        <Typography variant="h6" className={classes.filterName}>
          {renderContent(Dictionary.airStopsLabel.key, Dictionary.airStopsLabel.defaultValue)}
        </Typography>
        <div className={classes.filterContent}>
          <div className={classes.checkboxFilterContainer}>
            <Typography
              variant="body2"
              className={clsx(
                classes.filterText,
                stopsFilters.nonStop.disabled && classes.filterTextDisabled,
              )}>
              {renderContent(
                Dictionary.airNonstopLabel.key,
                Dictionary.airNonstopLabel.defaultValue,
              )}
            </Typography>
            <Checkbox
              className={clsx(
                classes.checkboxButton,
                stopsFilters.nonStop.disabled && classes.checkboxButtonDisabled,
              )}
              inputProps={{ 'data-testid': 'guest-checkbox-nonstop' }}
              onChange={(event) => toggleStopFilterSelection(event, 'nonStop')}
              disabled={stopsFilters.nonStop.disabled}
              checked={stopsFilters.nonStop.selected}
            />
          </div>
          <div className={classes.checkboxFilterContainer}>
            <Typography
              variant="body2"
              className={clsx(
                classes.filterText,
                stopsFilters.oneStop.disabled && classes.filterTextDisabled,
              )}>
              {renderContent(
                Dictionary.airOneStopLabel.key,
                Dictionary.airOneStopLabel.defaultValue,
              )}
            </Typography>
            <Checkbox
              className={clsx(
                classes.checkboxButton,
                stopsFilters.oneStop.disabled && classes.checkboxButtonDisabled,
              )}
              inputProps={{ 'data-testid': 'guest-checkbox-onestop' }}
              onChange={(event) => toggleStopFilterSelection(event, 'oneStop')}
              disabled={stopsFilters.oneStop.disabled}
              checked={stopsFilters.oneStop.selected}
            />
          </div>
          <div className={classes.checkboxFilterContainer}>
            <Typography
              variant="body2"
              className={clsx(
                classes.filterText,
                stopsFilters.twoStops.disabled && classes.filterTextDisabled,
              )}>
              {renderContent(
                Dictionary.airTwoOrMoreStopsLabel.key,
                Dictionary.airTwoOrMoreStopsLabel.defaultValue,
              )}
            </Typography>
            <Checkbox
              className={clsx(
                classes.checkboxButton,
                stopsFilters.twoStops.disabled && classes.checkboxButtonDisabled,
              )}
              inputProps={{ 'data-testid': 'guest-checkbox-twostops' }}
              onChange={(event) => toggleStopFilterSelection(event, 'twoStops')}
              disabled={stopsFilters.twoStops.disabled}
              checked={stopsFilters.twoStops.selected}
            />
          </div>
        </div>
      </div>
      <div className={classes.filterContainer}>
        <div className="time-header">
          <Typography variant="h6" className={classes.filterName}>
            {renderContent(Dictionary.airTimesLabel.key, Dictionary.airTimesLabel.defaultValue)}
          </Typography>
          <InfoTooltip
            tooltipOffsetLeft="100"
            description={renderContent(
              Dictionary.airFilterPanelTimesTooltipContent.key,
              Dictionary.airFilterPanelTimesTooltipContent.defaultValue,
            )}
          />
        </div>
        <div className={classes.filterContent}>
          <Typography
            variant="body2"
            className={clsx(
              classes.filterText,
              flightTimesFilters.takeOff.maxDateTime === 0 && classes.filterTextDisabled,
            )}>
            {`${renderContent(
              Dictionary.airTakeOffFromLabel.key,
              Dictionary.airTakeOffFromLabel.defaultValue,
            )} ${getAirportCode(true)}`}
          </Typography>
          {flightTimesFilters.takeOff.maxDateTime > 0 && (
            <Typography
              data-testid="take-off-times-filter-text"
              variant="body2"
              className={classes.datesRangeText}>
              {getTimeRangeLabelForFilter(
                localizedDayjs,
                takeOffTimesValue[0],
                takeOffTimesValue[1],
                abbreviatedWeekDays,
              )}
            </Typography>
          )}
          <div
            className={clsx(
              flightTimesFilters.takeOff.maxDateTime === 0
                ? classes.disabledSliderContainer
                : classes.sliderContainer,
              classes.takeOffTimeSliderContainer,
            )}>
            <SliderComponent
              data-testid="take-off-times-filter-slider"
              value={
                takeOffTimesValue.length === 2 && takeOffTimesValue[1] > 0
                  ? takeOffTimesValue
                  : [0, 1]
              }
              step={MILLISECONDS_IN_AN_HOUR}
              onChange={handleTakeOffTimesChange}
              onChangeCommitted={handleTakeOffTimesCommittedChange}
              aria-labelledby="continuous-slider"
              min={flightTimesFilters.takeOff.minDateTime}
              max={flightTimesFilters.takeOff.maxDateTime || 1}
              valueLabelDisplay="off"
              disabled={flightTimesFilters.takeOff.maxDateTime === 0}
            />
          </div>
          <Typography
            variant="body2"
            className={clsx(
              classes.filterText,
              flightTimesFilters.landing.maxDateTime === 0 && classes.filterTextDisabled,
            )}>
            {`${renderContent(
              Dictionary.airLandingLabel.key,
              Dictionary.airLandingLabel.defaultValue,
            )} ${getAirportCode(false)}`}
          </Typography>
          {flightTimesFilters.landing.maxDateTime > 0 && (
            <Typography
              data-testid="landing-times-filter-text"
              variant="body2"
              className={classes.datesRangeText}>
              {getTimeRangeLabelForFilter(
                localizedDayjs,
                landingTimesValue[0],
                landingTimesValue[1],
                abbreviatedWeekDays,
              )}
            </Typography>
          )}
          <div
            className={
              flightTimesFilters.landing.maxDateTime === 0
                ? classes.disabledSliderContainer
                : classes.sliderContainer
            }>
            <SliderComponent
              data-testid="landing-times-filter-slider"
              value={
                landingTimesValue.length === 2 && landingTimesValue[1] > 0
                  ? landingTimesValue
                  : [0, 1]
              }
              step={MILLISECONDS_IN_AN_HOUR}
              onChange={handleLandingTimesChange}
              onChangeCommitted={handleLandingTimesCommittedChange}
              aria-labelledby="continuous-slider"
              min={flightTimesFilters.landing.minDateTime}
              max={flightTimesFilters.landing.maxDateTime || 1}
              valueLabelDisplay="off"
              disabled={flightTimesFilters.landing.maxDateTime === 0}
            />
          </div>
        </div>
      </div>
      <div className={classes.filterContainer}>
        <Typography variant="h6" className={classes.filterName}>
          {renderContent(
            Dictionary.airPriceRangeLabel.key,
            Dictionary.airPriceRangeLabel.defaultValue,
          )}
        </Typography>
        <div className={classes.filterContent}>
          {minMaxPriceFilters.max > 0 && (
            <Typography
              data-testid="price-filter-text"
              variant="body2"
              className={classes.filterText}>
              {`${formatPrice(
                priceValue[0],
                minMaxPriceFilters.currency,
                false,
                true,
                false,
              )} - ${formatPrice(priceValue[1], minMaxPriceFilters.currency, false, true, false)}`}
            </Typography>
          )}
          <div
            className={
              minMaxPriceFilters.max === 0
                ? classes.disabledSliderContainer
                : classes.sliderContainer
            }>
            <SliderComponent
              data-testid="price-filter-slider"
              value={priceValue.length === 2 && priceValue[1] > 0 ? priceValue : [0, 1]}
              step={PRICE_FILTER_STEP}
              onChange={handlePriceChange}
              onChangeCommitted={handlePriceCommittedChange}
              aria-labelledby="continuous-slider"
              min={minMaxPriceFilters.min}
              max={minMaxPriceFilters.max || 1}
              valueLabelDisplay="off"
              disabled={minMaxPriceFilters.max === 0}
            />
          </div>
        </div>
      </div>
      <div className={classes.filterContainer}>
        <Typography variant="h6" className={classes.filterName}>
          {renderContent(Dictionary.airAirlinesLabel.key, Dictionary.airAirlinesLabel.defaultValue)}
        </Typography>
        <div className={classes.filterContent}>
          {(showAllAirlines ? airlineFilters : airlineFilters.slice(0, 4)).map((airlineFilter) => {
            return (
              <div key={airlineFilter.code} className={classes.checkboxFilterContainer}>
                <Typography variant="body2" className={clsx(classes.filterText)}>
                  {airlineFilter.name}
                </Typography>
                <Checkbox
                  className={clsx(classes.checkboxButton)}
                  inputProps={{ 'data-testid': `guest-checkbox-${airlineFilter.code}airline` }}
                  onChange={(event) => toggleAirlineFilterSelection(event, airlineFilter.code)}
                  checked={airlineFilter.selected}
                />
              </div>
            );
          })}
          {airlineFilters.length > 4 && (
            /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
            <Link
              data-testid="toggle-show-all-airlines-button"
              component="button"
              underline="none"
              className={`${classes.clearFiltersButton}`}
              onClick={toggleShowMoreFlag}>
              <span>
                {renderContent(
                  Dictionary[showAllAirlines ? 'airShowLessLabel' : 'airShowMoreLabel'].key,
                  Dictionary[showAllAirlines ? 'airShowLessLabel' : 'airShowMoreLabel']
                    .defaultValue,
                )}
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
